import axios from "axios";
import type { AxiosResponse } from "axios";
import config from "../config";
import type { ProblemDetails } from "../shared/types/problem-details.type";
import { store } from "../store/store";
import { accessToken } from "../shared/constants/localstorage.constant";
import { apiVersion } from "../shared/constants/common.constant";

/**
 * Creates a URL based on the provided parameters.
 *
 * @param url - The original URL to be transformed.
 * @param allowComplianceUrl - Optional flag to allow the compliance URL. If true, the original URL is returned.
 * @returns The transformed URL if `allowComplianceUrl` is false, otherwise the original URL.
 */
export const createUrl = (
  url: string,
  allowComplianceUrl?: boolean
): string => {
  const uppUrlReplacedUrl = url.replace(
    `${config.COMPLIANCE_API_URL}/${apiVersion}`,
    `${config.UPP_CONNECT_API_URL}/${apiVersion}/compliance/kyc-protect`
  );

  return allowComplianceUrl ? url : uppUrlReplacedUrl;
};

const addAuthHeader = (
  headers?: Record<string, string>
): Record<string, string> => {
  headers = headers ?? {};

  // User store accessToken when you integrate code with upp-authentication
  if (!headers.authorization && store.getState().user.accessToken) {
    headers.authorization = `Bearer ${store.getState().user.accessToken}`;
  }

  if (!headers.authorization && localStorage.getItem(accessToken)) {
    headers.authorization = `Bearer ${localStorage.getItem(accessToken)}`;
  }
  return headers;
};

/**
 * Sends an asynchronous GET request to the specified URL with the provided headers and signal.
 *
 * @param {string} url - The URL to which the GET request is sent.
 * @param {Record<string, string>} [headers] - An optional object containing request headers as key-value pairs.
 * @param {AbortSignal} [signal] - An optional AbortSignal to cancel the request.
 * @param {boolean} [allowComplianceUrl] - An optional flag to determine if the compliance URL should be used.
 * @returns {Promise<AxiosResponse<TDataType>>} - A promise that resolves to the response of the GET request.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getAsync = async <TDataType = any>(
  url: string,
  headers?: Record<string, string>,
  signal?: AbortSignal,
  allowComplianceUrl?: boolean
): Promise<AxiosResponse<TDataType>> =>
  await axios({
    method: "GET",
    url: createUrl(url, allowComplianceUrl),
    headers: addAuthHeader(headers),
    signal,
  });

/**
 * Sends an asynchronous POST request to the specified URL with the provided data and headers.
 *
 * @param {string} url - The URL to which the POST request is sent.
 * @param {unknown} [data] - The data to be sent in the body of the POST request. This parameter is optional.
 * @param {Record<string, string>} [headers] - An optional object containing request headers as key-value pairs.
 * @param {boolean} [allowComplianceUrl] - An optional flag to determine if the compliance URL should be used.
 * @returns {Promise<AxiosResponse<TDataType>>} - A promise that resolves to the response of the POST request.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const postAsync = async <TDataType = any>(
  url: string,
  data?: unknown,
  headers?: Record<string, string>,
  allowComplianceUrl?: boolean
): Promise<AxiosResponse<TDataType>> =>
  await sendAsync("POST", url, data, headers, allowComplianceUrl);

/**
 * Sends an asynchronous PUT request to the specified URL with the provided data and headers.
 *
 * @param {string} url - The URL to which the PUT request is sent.
 * @param {unknown} [data] - The data to be sent in the body of the PUT request. This parameter is optional.
 * @param {Record<string, string>} [headers] - An optional object containing request headers as key-value pairs.
 * @returns {Promise<AxiosResponse<TDataType>>} - A promise that resolves to the response of the PUT request.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const putAsync = async <TDataType = any>(
  url: string,
  data?: unknown,
  headers?: Record<string, string>,
  allowComplianceUrl?: boolean
): Promise<AxiosResponse<TDataType>> =>
  await sendAsync("PUT", url, data, headers, allowComplianceUrl);

/**
 * Sends an asynchronous PATCH request to the specified URL with the provided data and headers.
 *
 * @param {string} url - The URL to which the PATCH request is sent.
 * @param {unknown} [data] - The data to be sent in the body of the PATCH request. This parameter is optional.
 * @param {Record<string, string>} [headers] - An optional object containing request headers as key-value pairs.
 * @returns {Promise<AxiosResponse<TDataType>>} - A promise that resolves to the response of the PATCH request.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const patchAsync = async <TDataType = any>(
  url: string,
  data?: unknown,
  headers?: Record<string, string>,
  allowComplianceUrl?: boolean
): Promise<AxiosResponse<TDataType>> =>
  await sendAsync("PATCH", url, data, headers, allowComplianceUrl);

/**
 * Sends an asynchronous DELETE request to the specified URL with the provided data and headers.
 *
 * @param {string} url - The URL to which the DELETE request is sent.
 * @param {unknown} [data] - The data to be sent in the body of the DELETE request. This parameter is optional.
 * @param {Record<string, string>} [headers] - An optional object containing request headers as key-value pairs.
 * @returns {Promise<AxiosResponse<TDataType>>} - A promise that resolves to the response of the DELETE request.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deleteAsync = async <TDataType = any>(
  url: string,
  data?: unknown,
  headers?: Record<string, string>
): Promise<AxiosResponse<TDataType>> =>
  await sendAsync("DELETE", url, data, headers);

/**
 * Sends an asynchronous HTTP request to the specified URL with the provided method, data, and headers.
 *
 * @param {"PUT" | "POST" | "PATCH" | "DELETE"} method - The HTTP method to be used for the request.
 * @param {string} url - The URL to which the request is sent.
 * @param {unknown} [data] - The data to be sent in the body of the request. This parameter is optional.
 * @param {Record<string, string>} [headers] - An optional object containing request headers as key-value pairs.
 * @param {boolean} [allowComplianceUrl] - An optional flag to determine if the compliance URL should be used.
 * @returns {Promise<AxiosResponse<TDataType>>} - A promise that resolves to the response of the request.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sendAsync = async <TDataType = any>(
  method: "PUT" | "POST" | "PATCH" | "DELETE",
  url: string,
  data?: unknown,
  headers?: Record<string, string>,
  allowComplianceUrl?: boolean
): Promise<AxiosResponse<TDataType>> => {
  try {
    return await axios({
      method,
      url: createUrl(url, allowComplianceUrl),
      data,
      headers: addAuthHeader(headers),
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.response?.data) {
      const problemDetails: ProblemDetails = {
        ...error.response.data,
        status: error.response.status,
      };
      throw problemDetails;
    } else {
      const problemDetails: ProblemDetails = {
        type: "UnknownApiError",
        details: {
          title: "Unknown API Error",
          detail: `An unknown error occurred during an API call to the url ${url}`,
        },
      };
      throw problemDetails;
    }
  }
};
